/**
 * As mensagens de erro que o servidor manda são escritas em português (o servidor não sabe a
 * língua de cada pessoa). Aqui fica a tradução de cada uma para o inglês: o cliente mostra a da
 * língua escolhida. Uma mensagem nova no servidor precisa de uma linha aqui; o teste
 * apps/server/test/serverText.test.ts lê as mensagens do servidor e confere.
 */

/** As mensagens fixas, pelo texto exato em português. */
export const SERVER_TEXT_EN: Readonly<Record<string, string>> = {
  // contas, contatos e perfis
  'faça login para continuar': 'sign in to continue',
  'esta conta está suspensa': 'this account is suspended',
  'informe nome e senha': 'enter name and password',
  'nome ou senha incorretos': 'wrong name or password',
  'muitas tentativas de entrar nesta conta; espere alguns minutos': 'too many sign-in attempts for this account; wait a few minutes',
  'já existe um usuário com esse nome': 'there is already a user with that name',
  'já existe um usuário com esse nome ou e-mail': 'there is already a user with that name or e-mail',
  'esse e-mail já está em uso': 'that e-mail is already in use',
  'informe a senha atual e a nova': 'enter the current password and the new one',
  'senha atual incorreta': 'wrong current password',
  'informe a senha para confirmar': 'enter your password to confirm',
  // a confirmação do e-mail (docs/spec/email.md)
  'informe o e-mail': 'enter the e-mail',
  'e-mail inválido': 'invalid e-mail',
  'muitas contas criadas deste endereço; tente de novo mais tarde': 'too many accounts created from this address; try again later',
  'link de confirmação inválido': 'invalid confirmation link',
  'este link de confirmação não vale mais': 'this confirmation link is no longer valid',
  'este link de confirmação venceu': 'this confirmation link has expired',
  'este e-mail já está confirmado': 'this e-mail is already confirmed',
  'espere um pouco antes de pedir outro e-mail': 'wait a moment before asking for another e-mail',
  'esse já é o e-mail da conta': 'that is already the account e-mail',
  'confirme o seu e-mail para continuar': 'confirm your e-mail to continue',
  // os termos de uso e a política de privacidade (docs/spec/termos.md)
  'aceite os termos de uso e a política de privacidade': 'accept the terms of use and the privacy policy',
  'informe a versão dos termos': 'enter the version of the terms',
  'os termos mudaram desde que a página abriu; recarregue a página': 'the terms changed since the page was opened; reload the page',
  'senha incorreta': 'wrong password',
  'a senha precisa ter ao menos 8 caracteres': 'the password needs at least 8 characters',
  'a senha pode ter no máximo 200 caracteres': 'the password can have at most 200 characters',
  'a senha deve ser texto': 'the password must be text',
  'conta não encontrada': 'account not found',
  'usuário não encontrado': 'user not found',
  'informe a conta': 'enter the account',
  'sem avatar': 'no avatar',
  'sem imagem': 'no image',
  'não dá para adicionar você mesmo': 'you cannot add yourself',
  'vocês já são amigos': 'you are already friends',
  'não há pedido para aceitar': 'there is no request to accept',
  'não há relação com esse usuário': 'there is no relationship with this user',
  'só dá para esconder um amigo': 'only a friend can be hidden',
  'este amigo não está escondido': 'this friend is not hidden',
  'este usuário não está bloqueado': 'this user is not blocked',
  'não foi possível enviar o pedido para este usuário': 'could not send the request to this user',
  'não é possível enviar mensagens para este usuário': 'you cannot send messages to this user',
  'esta pessoa só recebe mensagens de amigos': 'this person only gets messages from friends',
  'você bloqueou esta pessoa': 'you blocked this person',
  'não dá para mandar mensagem para você mesmo': 'you cannot message yourself',
  'informe "ims" como "everyone" ou "friends"': 'send "ims" as "everyone" or "friends"',
  'informe a pessoa': 'tell us who',
  'entre na mesa para chamar amigos': 'join the room to invite friends',
  'escolha ao menos um amigo': 'choose at least one friend',
  'só dá para chamar amigos': 'you can only invite friends',
  'voto deve ser -1, 0 ou 1': 'the vote must be -1, 0 or 1',
  'informe "favorite" como booleano': 'send "favorite" as a boolean',
  'aba ou parâmetros inválidos': 'invalid tab or parameters',

  // salas
  'sala não encontrada': 'room not found',
  'entre em uma sala primeiro': 'join a room first',
  'senha da sala incorreta': 'wrong room password',
  'você foi banido desta sala': 'you were banned from this room',
  'essa pessoa não está na sala': 'that person is not in the room',
  'essa pessoa não está na mesa': 'that person is not in the room',
  'essa pessoa não está nesta sala': 'that person is not in this room',
  'esse jogador não está na sala': 'that player is not in the room',
  'escolha alguém que está na sala': 'choose someone who is in the room',
  'só o dono e os mestres auxiliares mudam a mesa': 'only the owner and the assistant game masters change the room',
  'só o dono e os mestres auxiliares veem estas listas': 'only the owner and the assistant game masters see these lists',
  'só o dono da sala escolhe os mestres auxiliares': 'only the room owner chooses the assistant game masters',
  'o dono da sala não entra nessa lista': 'the room owner cannot go on this list',
  'só o dono pode apagar a sala': 'only the owner can delete the room',
  'só o dono da sala pode fazer isso': 'only the room owner can do that',
  'só o mestre troca a foto dos outros': 'only the game master changes other people’s pictures',
  'ninguém muda o dono da sala': 'nobody changes the room owner',
  'mestres auxiliares continuam mestres enquanto estiverem na lista': 'assistant game masters stay game masters while they are on the list',
  'só mestres podem fazer isso': 'only game masters can do that',
  'endereço de imagem inválido': 'invalid image address',
  'endereço inválido': 'invalid address',
  'nada para mudar': 'nothing to change',

  // chat e conversas
  'mensagem vazia': 'empty message',
  'muitas mensagens em pouco tempo': 'too many messages in a short time',
  'um mestre tirou sua permissão de falar': 'a game master took away your permission to talk',
  'o chat está moderado': 'the chat is moderated',
  'só mestres podem moderar o chat': 'only game masters can moderate the chat',
  'só os mestres apagam mensagens do chat': 'only game masters delete chat messages',
  'só os mestres limpam o chat': 'only game masters clear the chat',
  'só os mestres veem as mensagens apagadas': 'only game masters see deleted messages',
  'mensagem não encontrada': 'message not found',
  'para sair, use /partir': 'to leave, use /leave',
  'conversa privada consigo mesmo': 'a private conversation with yourself',
  'você não está nessa conversa': 'you are not in that conversation',
  'não faz sentido sinalizar a si mesmo': 'you cannot signal yourself',

  // dados
  'muitas rolagens em pouco tempo': 'too many rolls in a short time',
  'expressão de dado inválida': 'invalid dice expression',
  'expressão de rolagem vazia': 'empty roll expression',
  'só pode existir 1 comparador na rolagem': 'a roll can only have 1 comparator',
  'faltou a face do dado (d20, d8, d6…)': 'the die is missing its number of faces (d20, d8, d6…)',
  'rolagem de dados desconhecida': 'unknown dice roll',
  'a rolagem precisa de pelo menos 1 dado': 'the roll needs at least 1 die',
  'rolagem grande demais': 'roll too large',
  'número grande demais na rolagem': 'number too large in the roll',

  // biblioteca, fichas e tabelas
  'item da biblioteca não encontrado': 'library item not found',
  'você não pode mudar este item': 'you cannot change this item',
  'pasta não encontrada': 'folder not found',
  'destino inválido': 'invalid destination',
  'documento não encontrado': 'document not found',
  'ficha não encontrada': 'sheet not found',
  'essa ficha não é sua': 'that sheet is not yours',
  'essa ficha não é dessa pessoa': 'that sheet is not that person’s',
  'só fichas têm dono': 'only sheets have an owner',
  'espectadores não criam fichas': 'spectators cannot create sheets',
  'só mestres criam pastas': 'only game masters create folders',
  'só mestres criam cenas': 'only game masters create scenes',
  'só mestres criam as macros da mesa': 'only game masters create the room’s macros',
  'macros da mesa não entram em pastas': 'the room’s macros cannot go into folders',
  'a mesa já tem macros': 'the room already has macros',
  'só mestres organizam a biblioteca': 'only game masters organize the library',
  'só mestres mudam o que os jogadores veem': 'only game masters change what players see',
  'só mestres mudam o dono da ficha': 'only game masters change the sheet’s owner',
  'só mestres escolhem o personagem dos outros': 'only game masters choose other people’s characters',
  'só mestres mudam o cartão dos outros': 'only game masters change other people’s cards',
  'este item não entra em pastas': 'this item cannot go into folders',
  'tabelas e macros são da mesa toda': 'tables and macros belong to the whole room',
  'só mestres ativam tabelas': 'only game masters enable tables',
  'só tabelas da sala se ativam e desativam': 'only room tables are enabled and disabled',
  'as tabelas do RPG Desk ficam sempre na mesa': 'the RPG Desk tables always stay in the room',
  'tabela sem tipo de dado': 'table without a data type',
  'esta tabela está desativada: reative-a': 'this table is disabled: enable it again',
  'esta tabela já está ativa': 'this table is already enabled',

  // plugins e webhooks
  'este servidor não aceita plugins web': 'this server does not accept web plugins',
  'só mestres instalam plugins na mesa': 'only game masters install plugins in the room',
  'só mestres removem plugins da mesa': 'only game masters remove plugins from the room',
  'arquivo do plugin não encontrado': 'plugin file not found',
  'plugin não encontrado': 'plugin not found',
  'erro ao servir o plugin': 'error serving the plugin',
  'só mestres veem os webhooks': 'only game masters see the webhooks',
  'só mestres cadastram webhooks': 'only game masters register webhooks',
  'o plugin não declara este webhook': 'the plugin does not declare this webhook',
  // plugins oficiais (docs/spec/web-plugins.md, "Plugins oficiais")
  'plugin oficial não encontrado': 'official plugin not found',
  'só os administradores da configuração do servidor mudam os plugins oficiais': 'only the administrators in the server configuration change the official plugins',
  'pedido de plugin inválido': 'invalid plugin request',
  'esse pacote já está publicado': 'this package is already published',
  'o pacote é de outro plugin': 'the package belongs to another plugin',
  'o plugin mudou enquanto você publicava: confira a lista e tente de novo': 'the plugin changed while you were publishing: check the list and try again',
  'não foi possível gravar o pacote': 'could not save the package',
  'nada mudou no plugin': 'nothing changed in the plugin',

  // cena
  'cena não encontrada': 'scene not found',
  'porta não encontrada': 'door not found',
  'efeito não encontrado': 'effect not found',
  'espectadores não pingam': 'spectators cannot ping',
  'só abre portas quem é mestre, jogador ou controla um token da cena': 'only game masters, players and whoever controls a token in the scene open doors',
  'token não encontrado': 'token not found',
  'só o mestre e quem controla o token o deixam invisível': 'only game masters and whoever controls the token make it invisible',
  'o invisível do token muda pelo menu do token': 'a token’s invisibility changes through the token’s menu',

  // áudio
  'só os mestres controlam o áudio da mesa': 'only game masters control the room’s audio',
  'só os mestres controlam o relógio da sessão': 'only game masters control the session clock',
  'só os mestres editam as sessões': 'only game masters edit the sessions',
  'só os mestres apagam as sessões': 'only game masters delete sessions',
  'o registro das sessões está desligado nesta mesa': 'session records are off in this room',
  'entre na mesa para ver as sessões': 'join the room to see its sessions',
  'título ou notas inválidos': 'invalid title or notes',
  'pedido de sessões inválido': 'invalid sessions request',
  'sessão não encontrada': 'session not found',
  'entre na mesa para avaliar a sessão': 'join the room to rate the session',
  'só quem jogou a sessão avalia': 'only those who played the session can rate it',
  'só os mestres avaliam os jogadores': 'only game masters rate the players',
  'esse jogador não esteve na sessão': 'that player was not in the session',
  'a avaliação abre quando a sessão termina': 'ratings open when the session ends',
  'a avaliação desta sessão já fechou': 'ratings for this session are closed',
  'avaliação inválida': 'invalid rating',
  'informe a faixa': 'choose the track',
  'faixa não encontrada': 'track not found',
  'não há faixa carregada': 'no track loaded',
  'a lista está cheia': 'the list is full',
  'seek exige positionSeconds': 'seek requires positionSeconds',

  // arquivos
  'arquivo não encontrado': 'file not found',
  'arquivo vazio': 'empty file',
  'arquivo maior que o limite': 'file larger than the limit',
  'o arquivo passa do limite do seu plano': "the file is over your plan's limit",
  'pedido de plano inválido': 'invalid plan request',
  'o prazo do plano precisa ser no futuro': "the plan's end date must be in the future",
  'o prazo do plano pode ser de no máximo 5 anos': "the plan's end date can be at most 5 years away",
  'o Grátis não tem prazo': 'the Free plan has no end date',
  'a conta já está nesse plano': 'the account is already on that plan',
  'plano não encontrado': 'plan not found',
  'só os administradores da configuração do servidor mudam os planos': "only the administrators in the server's configuration change the plans",
  'um valor do plano está fora da faixa permitida': "one of the plan's values is out of the allowed range",
  'o Grátis não tem preço': 'the Free plan has no price',
  'nada mudou no plano': 'nothing changed in the plan',
  'o nome digitado não é o da mesa': "the name you typed is not the room's",
  'um plano não pode dar menos que o plano abaixo dele': 'a plan cannot give less than the plan below it',
  'seu espaço de arquivos está cheio: apague arquivos ou mude de plano': 'your file space is full: delete files or change your plan',
  'o servidor está recebendo muitos arquivos; tente de novo em instantes': 'the server is receiving too many files; try again in a moment',
  'informe o nome do arquivo': 'enter the file name',
  'informe um nome de 1 a 200 caracteres': 'enter a name from 1 to 200 characters',
  'este arquivo não é seu': 'this file is not yours',
  'seu espaço de arquivos está cheio': 'your file space is full',
  'use uma imagem PNG, JPEG, WebP ou GIF': 'use a PNG, JPEG, WebP or GIF image',
  'a imagem da mesa pode ter até 5 MB': 'the room image can be at most 5 MB',
  'a capa pode ter até 5 MB': 'the cover can be at most 5 MB',
  'não foi possível gravar o arquivo': 'could not save the file',
  'não foi possível gravar a imagem': 'could not save the image',
  'link expirado ou inválido': 'expired or invalid link',
  'informe a sala (ou null para desvincular)': 'send the room (or null to unlink)',
  'só o dono da sala pode vincular arquivos a ela': 'only the room owner can link files to it',
  // Desk Cloud (docs/spec/desk-cloud.md)
  'pedido de arquivos inválido': 'invalid files request',
  'informações do arquivo inválidas': 'invalid file details',
  'informe um nome de 1 a 80 caracteres': 'enter a name from 1 to 80 characters',
  'uma pasta não pode ir para dentro dela mesma': 'a folder cannot go inside itself',
  'já existe uma pasta com esse nome aqui': 'there is already a folder with that name here',
  'só dá para excluir para sempre o que está na lixeira': 'only what is in the trash can be deleted forever',
  'só os mestres da mesa ligam arquivos a ela': "only the room's game masters can link files to it",
  'não há arquivos para o zip': 'there are no files for the zip',
  'o zip pode ter até 2 GB': 'the zip can have up to 2 GB',
  'espere o zip anterior terminar': 'wait for the previous zip to finish',
  'muitas imagens buscadas; espere um pouco': 'too many images fetched; wait a little',
  'este arquivo não leva miniatura': 'this file does not take a thumbnail',
  'use uma imagem PNG, JPEG ou WebP': 'use a PNG, JPEG or WebP image',
  'a miniatura passa do limite': 'the thumbnail is over the limit',

  // o resto
  'dados inválidos': 'invalid data',
  'mensagem inválida': 'invalid message',
  'mensagem não é JSON válido': 'the message is not valid JSON',
  'rota não encontrada': 'route not found',
  'erro inesperado no servidor': 'unexpected server error',
  'candidate exige candidate; offer/answer exigem sdp': 'candidate requires candidate; offer/answer require sdp',
  // área de administração (docs/spec/admin.md)
  'só administradores podem fazer isso': 'only administrators can do that',
  'busca inválida': 'invalid search',
  'a conta não está suspensa': 'the account is not suspended',
  'o nome digitado não é o da conta': "the name typed is not the account's",
  'a conta já é administradora': 'the account is already an administrator',
  'a conta não é administradora': 'the account is not an administrator',
  'você não pode fazer isso com a sua própria conta': 'you cannot do that to your own account',
  'esta conta é administradora pela configuração do servidor': 'this account is an administrator by the server configuration',
  'tire o papel de administrador desta conta antes': 'remove the administrator role from this account first',
  'reative a conta antes': 'reactivate the account first',
};

/** As mensagens com uma parte que muda (um nome, um tipo, o motivo). */
const SERVER_PATTERNS_EN: readonly (readonly [RegExp, string])[] = [
  [/^esta conta está suspensa: ([\s\S]*)$/, 'this account is suspended: $1'],
  [/^o motivo pode ter no máximo (\S+) caracteres$/, 'the reason can have at most $1 characters'],
  [/^o plugin não foi instalado: ([\s\S]*)$/, 'the plugin was not installed: $1'],
  [/^o pacote não foi aceito: ([\s\S]*)$/, 'the package was not accepted: $1'],
  [/^o pacote passa de (\S+) MB$/, 'the package is over $1 MB'],
  [/^a versão (\S+) já foi publicada: aumente a versão no manifest\.json$/, 'version $1 was already published: raise the version in manifest.json'],
  [/^a versão publicada é (\S+): envie uma versão maior$/, 'the published version is $1: send a higher version'],
  [/^o plugin oficial ainda está em uso \(mesas: (\S+)\): oculte-o do catálogo em vez de apagar$/, 'the official plugin is still in use (rooms: $1): hide it from the catalog instead of deleting it'],
  [/^você chegou ao limite de (\S+) pastas$/, 'you reached the limit of $1 folders'],
  [/^as pastas podem ter no máximo (\S+) níveis$/, 'folders can have at most $1 levels'],
  [/^o zip pode ter até (\S+) arquivos$/, 'the zip can have up to $1 files'],
  [/^no máximo (\S+) imagens do Desk Cloud por mensagem$/, 'at most $1 Desk Cloud images per message'],
  [/^o tipo "(.*)" já é do plugin "(.*)" nesta mesa$/, 'the type "$1" already belongs to the plugin "$2" in this room'],
  [/^não deu para mudar o dono: (.*)$/, 'could not change the owner: $1'],
  [/^comparador desconhecido: "(.*)"$/, 'unknown comparator: "$1"'],
  [/^caractere desconhecido na rolagem: "(.*)"$/, 'unknown character in the roll: "$1"'],
  [/^tipo não tratado: (.*)$/, 'unhandled type: $1'],
];

/**
 * A mensagem do servidor na língua de quem lê. Sem tradução conhecida (um texto que não veio do
 * servidor, por exemplo), devolve como veio.
 */
export function serverText(message: string, language: string): string {
  if (!language.toLowerCase().startsWith('en')) return message;
  const exact = SERVER_TEXT_EN[message];
  if (exact !== undefined) return exact;
  for (const [pattern, english] of SERVER_PATTERNS_EN) if (pattern.test(message)) return message.replace(pattern, english);
  return message;
}
